import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { nestedLocationsApi } from '../axios/nestedLocations';
import { useLanguage } from '../hooks';
import { NestedLocationPayload } from '../types/nestedLocations';

export function useGetNestedLocation(id: number | undefined) {
  return useQuery({
    queryKey: ['nestedLocation', id],
    queryFn: () => {
      if (!id) throw new Error('ID is required');
      return nestedLocationsApi.getNestedLocationById(id);
    },
    enabled: !!id,
  });
}

export function useAllNestableLocationsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['allLocations'],
    queryFn: () => nestedLocationsApi.getAllLocations(),
    enabled,
  });
}

const DELAYED_REFETCH_AFTER_DELETE_MS = 3000;

export function useDeleteLocationMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (locationId: number) => {
      return nestedLocationsApi.deleteNestedLocations(locationId);
    },
    onSuccess: () => {
      // Note: We should not invalidate locationsIndex query here because it will cause a re-fetch of all locations and the expanded locations will be lost.
      queryClient.invalidateQueries({ queryKey: ['accountStatusWithLocations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['allLocations'], exact: false });

      // Delayed refetch to catch async backend processes that might take time to complete
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['allLocations'], exact: false });
        queryClient.invalidateQueries({ queryKey: ['accountStatusWithLocations'], exact: false });
      }, DELAYED_REFETCH_AFTER_DELETE_MS);

      enqueueSnackbar(lang.locations.deleteConfirmation.success, { variant: 'success' });
    },
    onError: (error) => {
      console.error('Error deleting location:', error);
      const message = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
      const errorMessage = message ? `: ${message}` : '';
      enqueueSnackbar(lang.locations.deleteConfirmation.error + errorMessage, { variant: 'error' });
    },
  });
}

export function useCreateLocationMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: NestedLocationPayload) => {
      return nestedLocationsApi.createNestedLocation(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locationsIndex'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['accountStatusWithLocations'], exact: false });
      enqueueSnackbar(lang.locations.create.success, { variant: 'success' });
    },
    onError: (error) => {
      const message = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
      const errorMessage = message ? `: ${message}` : '';
      console.error('Error creating location:', error, message);
      enqueueSnackbar(lang.locations.create.error + errorMessage, { variant: 'error' });
    },
  });
}

export function useUpdateLocationMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<NestedLocationPayload> }) => {
      return nestedLocationsApi.updateNestedLocation(id, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['locationsIndex'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['accountStatusWithLocations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['nestedLocation', variables.id] });
      enqueueSnackbar(lang.locations.update.success, { variant: 'success' });
    },
    onError: (error) => {
      console.error('Error updating location:', error);
      const message = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
      const errorMessage = message ? `: ${message}` : '';
      enqueueSnackbar(lang.locations.update.error + errorMessage, { variant: 'error' });
    },
  });
}

export function useImportNestedLocationsMutation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const lang = useLanguage();

  const mutation = useMutation({
    mutationFn: (file: File) => {
      return nestedLocationsApi.importNestedLocations(file);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['locationsIndex'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['accountStatusWithLocations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['allLocations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['nestedLocations'], exact: false });
      enqueueSnackbar(data.message, { variant: 'success' });
      setErrorMessage(null);
    },
    onError: (error: AxiosError<{ message?: string; error?: string }>) => {
      let errorMsg = lang.locations.import.errors.importError;

      if (error && typeof error === 'object' && 'response' in error) {
        const response = error.response;
        errorMsg = response?.data?.message || response?.data?.error || errorMsg;
      }
      setErrorMessage(errorMsg);
    },
  });

  return {
    ...mutation,
    errorMessage,
  };
}
